/**
 * FacturationPage — issue an invoice (POST /factures, cap finance.facture.issue) with line items +
 * an optional remise (percentage or fixed amount), then read the issued invoice back inline.
 *
 * The server contract for /factures only mandates `lignes:[{prix}]` (+ optional demande_analyse_id,
 * remise). There is NO GET endpoint to list/fetch invoices, so "facture read" = the issued payload
 * echoed read-only after POST (held in component state). A "collect now" hand-off jumps the user to
 * the caisse encaissement form with this invoice prefilled — but ONLY when a session is open.
 *
 * 5-state DoD: sans-droit (ProtectedRoute on finance.facture.issue + ForbiddenState fallback) and
 * hors-ligne (OfflineState) are handled here; the composer itself is a form (no list to load), so
 * chargement/erreur/vide are surfaced on submit (toast + inline errors) rather than as a fetch.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  FormField,
  ForbiddenState,
  Input,
  OfflineState,
  Select,
  Table,
  Textarea,
  useToast,
  type Column,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { ApiError } from "@/api";
import { FINANCE_CAPS, useCurrentSession, useIssueFacture, useLabId } from "./api";
import type { Facture, FactureLigne, Remise, RemiseType } from "./types";
import { sumCentimes } from "./money";
import { MoneyInput } from "./components/MoneyInput";
import { Money } from "./components/Money";

interface DraftLigne {
  id: string;
  libelle: string;
  prix: number | null;
}

let ligneSeq = 0;
const newLigne = (): DraftLigne => ({ id: `l_${++ligneSeq}`, libelle: "", prix: null });

export function FacturationPage() {
  const { t } = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);
  const canIssue = useCan(FINANCE_CAPS.factureIssue);
  const canCollect = useCan(FINANCE_CAPS.paiementCollect);

  const mutation = useIssueFacture();
  // Used only to decide whether the "collect now" hand-off is reachable (an open session exists).
  const sessionQuery = useCurrentSession(labId, online && canCollect);

  const [demandeId, setDemandeId] = useState("");
  const [lignes, setLignes] = useState<DraftLigne[]>([newLigne()]);
  const [remiseType, setRemiseType] = useState<"none" | RemiseType>("none");
  const [remisePct, setRemisePct] = useState<string>("");
  const [remiseMontant, setRemiseMontant] = useState<number | null>(null);
  const [remiseMotif, setRemiseMotif] = useState("");
  const [touched, setTouched] = useState(false);
  const [issued, setIssued] = useState<Facture | null>(null);

  const totalBrut = useMemo(
    () => sumCentimes(lignes.map((l) => l.prix ?? 0)),
    [lignes],
  );

  const pctValue = remisePct.trim() === "" ? null : Number(remisePct.replace(",", "."));
  const pctInvalid =
    remiseType === "pourcentage" && (pctValue == null || !Number.isFinite(pctValue) || pctValue < 0 || pctValue > 100);

  const remiseMontantCentimes = useMemo(() => {
    if (remiseType === "pourcentage") {
      if (pctValue == null || !Number.isFinite(pctValue)) return 0;
      return Math.round((totalBrut * Math.min(Math.max(pctValue, 0), 100)) / 100);
    }
    if (remiseType === "montant") return remiseMontant ?? 0;
    return 0;
  }, [remiseType, pctValue, totalBrut, remiseMontant]);

  const remiseTooBig = remiseMontantCentimes > totalBrut;
  const totalNet = Math.max(0, totalBrut - remiseMontantCentimes);

  const hasLigne = lignes.some((l) => l.prix != null && l.prix > 0);
  const lignesError = touched && !hasLigne ? t("finance.facture.ligne.none") : null;

  const remiseValid =
    remiseType === "none" ||
    (remiseType === "pourcentage" && !pctInvalid && !remiseTooBig) ||
    (remiseType === "montant" && remiseMontant != null && remiseMontant >= 0 && !remiseTooBig);

  const valid = hasLigne && remiseValid;

  const remiseTypeOptions = [
    { value: "none", label: t("finance.facture.remise.none") },
    { value: "pourcentage", label: t("finance.facture.remise.pourcentage") },
    { value: "montant", label: t("finance.facture.remise.montant") },
  ];

  const updateLigne = (id: string, patch: Partial<DraftLigne>) =>
    setLignes((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const addLigne = () => setLignes((prev) => [...prev, newLigne()]);
  const removeLigne = (id: string) =>
    setLignes((prev) => (prev.length > 1 ? prev.filter((l) => l.id !== id) : prev));

  const resetForm = () => {
    setDemandeId("");
    setLignes([newLigne()]);
    setRemiseType("none");
    setRemisePct("");
    setRemiseMontant(null);
    setRemiseMotif("");
    setTouched(false);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !canIssue) return;

    const payloadLignes: FactureLigne[] = lignes
      .filter((l) => l.prix != null && l.prix > 0)
      .map((l) => ({ prix: l.prix as number, ...(l.libelle.trim() ? { libelle: l.libelle.trim() } : {}) }));

    let remise: Remise | undefined;
    if (remiseType === "pourcentage" && pctValue != null) {
      remise = { type: "pourcentage", valeur: pctValue, ...(remiseMotif.trim() ? { motif: remiseMotif.trim() } : {}) };
    } else if (remiseType === "montant" && remiseMontant != null && remiseMontant > 0) {
      remise = { type: "montant", valeur: remiseMontant, ...(remiseMotif.trim() ? { motif: remiseMotif.trim() } : {}) };
    }

    mutation.mutate(
      {
        ...(demandeId.trim() ? { demande_analyse_id: demandeId.trim() } : {}),
        lignes: payloadLignes,
        ...(remise ? { remise } : {}),
      },
      {
        onSuccess: (data) => {
          toast.success(t("finance.facture.success"));
          setIssued(data);
          resetForm();
        },
        onError: (err) => {
          toast.error(err instanceof ApiError ? err.message : t("finance.facture.submit"));
        },
      },
    );
  };

  const ligneColumns: ReadonlyArray<Column<DraftLigne>> = [
    {
      key: "libelle",
      header: t("finance.facture.ligne.libelle"),
      cell: (row) => (
        <Input
          aria-label={t("finance.facture.ligne.libelle")}
          value={row.libelle}
          autoComplete="off"
          onChange={(e) => updateLigne(row.id, { libelle: e.target.value })}
        />
      ),
    },
    {
      key: "prix",
      header: t("finance.facture.ligne.prix"),
      align: "end",
      width: "220px",
      cell: (row) => (
        <MoneyInput
          aria-label={t("finance.facture.ligne.prix")}
          valueCentimes={row.prix}
          onChangeCentimes={(c) => updateLigne(row.id, { prix: c })}
        />
      ),
    },
    {
      key: "actions",
      header: "",
      align: "end",
      width: "56px",
      cell: (row) => (
        <Button
          variant="ghost"
          size="sm"
          aria-label={t("finance.facture.ligne.remove")}
          disabled={lignes.length <= 1}
          onClick={() => removeLigne(row.id)}
        >
          ✕
        </Button>
      ),
    },
  ];

  const header = (
    <header style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-1)" }}>
      <h1 style={{ fontSize: "var(--nv-fs-h1)", margin: 0 }}>{t("finance.facture.title")}</h1>
      <p style={{ margin: 0, color: "var(--nv-slate)" }}>{t("finance.facture.subtitle")}</p>
    </header>
  );

  if (!canIssue) {
    return <ForbiddenState requiredCapabilities={[FINANCE_CAPS.factureIssue]} />;
  }
  if (!online) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
        {header}
        <OfflineState />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      {header}

      <form onSubmit={submit} noValidate>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
          <Card title={t("finance.facture.lignes")}>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
              <FormField
                label={t("finance.facture.demandeId")}
                hint={t("finance.facture.demandeId.hint")}
              >
                {(p) => (
                  <Input
                    {...p}
                    mono
                    className="nv-ltr-island"
                    autoComplete="off"
                    value={demandeId}
                    style={{ maxWidth: 360 }}
                    onChange={(e) => setDemandeId(e.target.value)}
                  />
                )}
              </FormField>

              <Table<DraftLigne>
                columns={ligneColumns}
                rows={lignes}
                rowKey={(r) => r.id}
                caption={t("finance.facture.lignes")}
              />
              {lignesError && (
                <span role="alert" style={{ color: "var(--nv-danger)", fontSize: "var(--nv-fs-label)" }}>
                  {lignesError}
                </span>
              )}

              <div>
                <Button variant="secondary" size="sm" onClick={addLigne}>
                  + {t("finance.facture.ligne.add")}
                </Button>
              </div>
            </div>
          </Card>

          <Card title={t("finance.facture.remise")}>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)", maxWidth: 480 }}>
              <FormField label={t("finance.facture.remise.type")}>
                {(p) => (
                  <Select
                    {...p}
                    options={remiseTypeOptions}
                    value={remiseType}
                    onChange={(e) => setRemiseType(e.target.value as "none" | RemiseType)}
                  />
                )}
              </FormField>

              {remiseType === "pourcentage" && (
                <FormField
                  label={t("finance.facture.remise.valeurPct")}
                  required
                  {...(touched && pctInvalid
                    ? { error: t("finance.facture.remise.pctInvalid") }
                    : remiseTooBig
                      ? { error: t("finance.facture.remise.tooBig") }
                      : {})}
                >
                  {(p) => (
                    <Input
                      {...p}
                      mono
                      className="nv-ltr-island"
                      inputMode="decimal"
                      autoComplete="off"
                      value={remisePct}
                      invalid={(touched && pctInvalid) || remiseTooBig}
                      placeholder="0"
                      style={{ maxWidth: 140, textAlign: "end" }}
                      onChange={(e) => setRemisePct(e.target.value)}
                    />
                  )}
                </FormField>
              )}

              {remiseType === "montant" && (
                <FormField
                  label={t("finance.facture.remise.valeurMontant")}
                  required
                  {...(remiseTooBig ? { error: t("finance.facture.remise.tooBig") } : {})}
                >
                  {(p) => (
                    <MoneyInput
                      {...p}
                      aria-label={t("finance.amount.aria")}
                      valueCentimes={remiseMontant}
                      onChangeCentimes={setRemiseMontant}
                      invalid={remiseTooBig}
                    />
                  )}
                </FormField>
              )}

              {remiseType !== "none" && (
                <FormField
                  label={t("finance.facture.remise.motif")}
                  hint={t("finance.facture.remise.motif.hint")}
                >
                  {(p) => (
                    <Textarea
                      {...p}
                      rows={2}
                      value={remiseMotif}
                      onChange={(e) => setRemiseMotif(e.target.value)}
                    />
                  )}
                </FormField>
              )}
            </div>
          </Card>

          <Card title={t("finance.facture.totalNet")}>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
              <TotalRow label={t("finance.facture.totalBrut")} centimes={totalBrut} />
              {remiseMontantCentimes > 0 && (
                <TotalRow label={t("finance.facture.remise")} centimes={-remiseMontantCentimes} signed />
              )}
              <div style={{ height: 1, background: "var(--nv-line)" }} />
              <TotalRow label={t("finance.facture.totalNet")} centimes={totalNet} strong />

              <div style={{ display: "flex", justifyContent: "flex-end", marginBlockStart: "var(--nv-s-2)" }}>
                <Button type="submit" loading={mutation.isPending} disabled={!canIssue}>
                  {t("finance.facture.submit")}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </form>

      {issued && (
        <Card
          title={t("finance.facture.issued.title")}
          actions={<Badge tone="success">{t("finance.facture.success")}</Badge>}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
            <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "var(--nv-s-2) var(--nv-s-5)", margin: 0 }}>
              <dt style={{ color: "var(--nv-slate)" }}>{t("finance.facture.numero")}</dt>
              <dd className="nv-mono nv-ltr-island" style={{ margin: 0 }}>
                {issued.numero ?? issued.id}
              </dd>
              <dt style={{ color: "var(--nv-slate)" }}>{t("finance.facture.totalBrut")}</dt>
              <dd style={{ margin: 0 }}>
                <Money centimes={issued.total_ht} />
              </dd>
              {issued.total_remise != null && issued.total_remise > 0 && (
                <>
                  <dt style={{ color: "var(--nv-slate)" }}>{t("finance.facture.remise")}</dt>
                  <dd style={{ margin: 0 }}>
                    <Money centimes={-issued.total_remise} signed />
                  </dd>
                </>
              )}
              <dt style={{ color: "var(--nv-slate)" }}>{t("finance.facture.totalNet")}</dt>
              <dd style={{ margin: 0 }}>
                <Money centimes={issued.total_ttc} strong />
              </dd>
            </dl>

            <div style={{ display: "flex", gap: "var(--nv-s-2)" }}>
              <Button
                variant="secondary"
                disabled={!canCollect || !sessionQuery.data}
                onClick={() =>
                  navigate("/finance/caisse", {
                    state: {
                      collectFactureId: issued.id,
                      collectMontant: issued.total_ttc ?? null,
                    },
                  })
                }
              >
                {t("finance.facture.payNow")}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

function TotalRow({
  label,
  centimes,
  strong,
  signed,
}: {
  label: string;
  centimes: number;
  strong?: boolean;
  signed?: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
      <span style={{ color: strong ? "var(--nv-ink)" : "var(--nv-slate)", fontWeight: strong ? 600 : 400 }}>
        {label}
      </span>
      <span style={{ fontSize: strong ? "var(--nv-fs-title)" : "var(--nv-fs-body)" }}>
        <Money centimes={centimes} strong={strong} signed={signed} />
      </span>
    </div>
  );
}
